import '@/services/buffer-polyfill'

import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
  sum,
  fetchWithRetry,
  createBundleSelection,
  sortBundleProductSelections,
  getBundleSellingPlanId,
  bundleJSONContents
} from '@/utils'
import { useBundleStore, useCartStore, useGiftCreateStore, useReferralStore, useSpecialDiscountStore, useProteinPackUpsellStore } from '@/stores'
import { initRecharge, getBundleId } from '@rechargeapps/storefront-client'
import { useFueled } from '@/services/fueled'
import { DOMAIN, CHECKOUT_DOMAIN } from '@/services/constants'
import { CART_ITEM_ADD, track } from '@/services/events'
import { useAmplitude } from '@/services/amplitude'
import { compact, timer, setUrlParam } from '@/utils'
import type { BundlePlan } from '@/types/bundle'

export const useBundleCheckoutStore = defineStore('bundle/checkout', () => {
  const bundleStore = useBundleStore()
  const cartStore = useCartStore()
  const proteinPackStore = useProteinPackUpsellStore()
  const referralStore = useReferralStore()
  const discountStore = useSpecialDiscountStore()
  const { buildGiftItem } = useGiftCreateStore()
  const { trackAddToCart } = useFueled()
  const { trackCart } = useAmplitude()

  let timeout: ReturnType<typeof setTimeout>
  const loading = ref(false)

  const nonBundleOrder = computed(() => bundleStore.totalQuantity === 0 && cartStore.items.length > 0)
  const totalCents = computed(() => (nonBundleOrder.value ? 0 : bundleStore.priceCents) + sum(...cartStore.nonBundleItems.map(i => i.line_price)))

  const buildBundleItem = async () => {
    initRecharge({ storeIdentifier: DOMAIN })
    // Get product, variant & selection
    const { product, variant, selectedProducts, plan, checkoutSwitchToSubscription } = bundleStore
    // Validate
    if (!product || !variant || !selectedProducts) {
      throw new Error('could not create bundle')
    }
    // Check if checking out switching from OneTime to Subscription
    const bundlePlan = checkoutSwitchToSubscription ? 'subscription' : plan
    // Sort bundle selections
    const sorted = sortBundleProductSelections(selectedProducts)
    // Create Recharge bundle selection
    const bundle = createBundleSelection(product, variant, sorted)
    // TODO: cache bundle ID if selection does not change?
    // Create Recharge bundle ID
    const bundleId = await getBundleId(bundle)
    // Create Shopify Cart Item
    const item = {
      id: variant.nid,
      sku: variant.sku,
      selling_plan: getBundleSellingPlanId(product, bundlePlan),
      quantity: 1,
      properties: {
        _rb_id: bundleId,
        _contents: bundleJSONContents(sorted),
        _v3: true
      }
    }
    return item
  }

  const getCartAttributes = () => {
    return compact({
      domain: CHECKOUT_DOMAIN,
      referral: referralStore.code,
      referrer_address_fingerprint: referralStore.fingerprint,
      special_discount: discountStore.discountCode
    })
  }

  const addToCart = async (switchPlan?: BundlePlan) => {
    // Set loading
    loading.value = true

    if (switchPlan === 'subscription') {
      bundleStore.checkoutSwitchToSubscription = true
      bundleStore.plan = 'subscription'
      setUrlParam('type', 'subscription')
    } else if (switchPlan === 'onetime') {
      bundleStore.checkoutSwitchToSubscription = false
      bundleStore.plan = 'onetime'
      setUrlParam('type', 'onetime')
    }

    const checkoutAsSub = switchPlan === 'subscription' || bundleStore.checkoutSwitchToSubscription
    const { product, priceCents } = bundleStore
    // Map existing non-bundle items (protein pack is already in cart if staged)
    const items = [ ...cartStore.nonBundleItems ].map(i => {
      // When checking out as subscription, upgrade protein pack from one-time to subscription
      if (checkoutAsSub && i.handle === 'protein-pack' && !i.selling_plan_allocation && proteinPackStore.sellingPlanId) {
        return { id: i.variant_id, quantity: i.quantity, selling_plan: proteinPackStore.sellingPlanId }
      }
      return {
        id: i.variant_id,
        quantity: i.quantity,
        ...(i.selling_plan_allocation?.selling_plan?.id && { selling_plan: i.selling_plan_allocation.selling_plan.id })
      }
    })
    // Convert bundle selection to Shopify cart item
    const item = await fetchWithRetry(buildBundleItem)
    items.push(item)
    // Reset cart
    await fetch('/cart/clear.js')
    // Add items to cart
    await Promise.all([
      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items,
          attributes: getCartAttributes()
        })
      }),
      // @ts-ignore
      trackAddToCart(product, item, 1, priceCents)
    ])
    const cart = await fetch('/cart.json')
    track(CART_ITEM_ADD, cart)
    await trackCart(await cart.json())

    await redirectToCheckout()
  }

  const addGiftToCart = async () => {
    loading.value = true
    await fetch('/cart/clear.js')
    await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items: [ buildGiftItem() ] })
    })
    const cart = await fetch('/cart.json')
    track(CART_ITEM_ADD, cart)
    await trackCart(await cart.json())
    await redirectToCheckout()
  }

  const redirectToCheckout = async () => {
    await timer(200 )
    // Set timeout to reset loading after 3s
    clearTimeout(timeout)
    timeout = setTimeout(() => loading.value = false, 3000)

    // Redirect to checkout
    const url = new URL('/checkout', window.location.origin)
    window.location.href = url.toString()
  }

  return {
    loading,
    totalCents,
    nonBundleOrder,
    addToCart,
    addGiftToCart,
    redirectToCheckout
  }
})
